import { useMemo } from "react";

import { useDynamicConfig, useStatsigClient } from "@statsig/react-bindings";

import { useIsLoggedIn } from "~/queries/profile/useProfile";

import {
  AI_ANSWERS_COHORT_CONFIG,
  AI_ANSWERS_COHORT_LISTING_IDS_KEY,
  AI_ANSWERS_EXPERIMENT,
  AI_ANSWERS_SHOW_MODAL_KEY,
} from "./ai-instant-answers.consts";

/**
 * The cohort gate plus the A/B split. Returns true only when the guest is logged in,
 * the listing is in the Ops-curated Statsig cohort, AND the guest landed in the test
 * arm of `ai_instant_responses`. Logged-out visitors always get false, so the CTA
 * never opens the modal for them. Clearing the config flips every listing to false on
 * the next render, no deploy.
 *
 * The experiment is only evaluated — and so only logs an exposure — once the guest
 * is eligible.
 */
const useAiAnswersEnabled = (listingId: string): boolean => {
  const isLoggedIn = useIsLoggedIn();
  const cohortConfig = useDynamicConfig(AI_ANSWERS_COHORT_CONFIG);
  const { getExperiment } = useStatsigClient();

  const cohortListingIds = cohortConfig.get<string[]>(
    AI_ANSWERS_COHORT_LISTING_IDS_KEY,
    [],
  );
  const isInCohort = isLoggedIn && cohortListingIds.includes(listingId);

  // `getExperiment` changes identity on every Statsig re-render, so this
  // re-reads after a user update just like `useExperiment` would. The SDK
  // dedupes exposures per user, so re-reads don't log duplicates.
  return useMemo(
    () =>
      isInCohort &&
      getExperiment(AI_ANSWERS_EXPERIMENT).get(
        AI_ANSWERS_SHOW_MODAL_KEY,
        false,
      ),
    [getExperiment, isInCohort],
  );
};

export { useAiAnswersEnabled };
